// src/components/Overview.jsx
import React, { useState, useEffect } from "react";
import MetricCard from "./MetricCard";
import ChartModal from "./ChartModal";
import YieldCurveChart from "./YieldCurveChart";
import "./Overview.css";
import { apiUrl } from "../apiBase";

/* =========================
   Helpers for Market Tone
   ========================= */
const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n);

const pctChange = (value, prevValue) => {
  if (!isFiniteNumber(value) || !isFiniteNumber(prevValue) || prevValue === 0) return null;
  return ((value - prevValue) / prevValue) * 100;
};

function computeMarketTone(overview) {
  const spxPct = pctChange(overview?.sp500?.value, overview?.sp500?.prevValue);
  const dxyPct = pctChange(overview?.dxy?.value, overview?.dxy?.prevValue);

  const y2 = overview?.yield2y?.value;
  const y10 = overview?.yield10y?.value;
  const curve = isFiniteNumber(y10) && isFiniteNumber(y2) ? y10 - y2 : null;

  let score = 0;

  // 1) Stocks daily momentum (SPX)
  if (spxPct !== null) {
    if (spxPct > 0.4) score += 1;
    else if (spxPct < -0.4) score -= 1;
  }

  // 2) Curve slope (10Y-2Y): positive = healthier, inverted = tighter
  if (curve !== null) {
    if (curve > 0.15) score += 0.75;
    else if (curve < -0.15) score -= 0.75;
  }

  // 3) Dollar strength (DXY): strong up can be tightening / risk-off-ish
  if (dxyPct !== null) {
    if (dxyPct > 0.25) score -= 0.5;
    else if (dxyPct < -0.25) score += 0.5;
  }

  if (score >= 1.25) {
    return {
      label: "Risk-On",
      detail: "Momentum and conditions lean supportive for risk assets.",
      score,
      inputs: { spxPct, curve, dxyPct },
    };
  }

  if (score <= -1.25) {
    return {
      label: "Risk-Off",
      detail: "Signals lean defensive: tighter conditions or weak momentum.",
      score,
      inputs: { spxPct, curve, dxyPct },
    };
  }

  return {
    label: "Neutral",
    detail: "Signals are mixed — neither strong fear nor strong greed.",
    score,
    inputs: { spxPct, curve, dxyPct },
  };
}

/* =========================
   Overview Component
   ========================= */
const Overview = ({ onConnectionStatus }) => {
  const [overviewData, setOverviewData] = useState(null);
  const [overviewHistory, setOverviewHistory] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);

  const [marketTone, setMarketTone] = useState({
    label: "Neutral",
    detail: "",
    score: 0,
    inputs: { spxPct: null, curve: null, dxyPct: null },
  });

  const [activeInfoMetric, setActiveInfoMetric] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // Metric configuration – ids MUST match keys from /api/overview-metrics
  const metricsConfig = {
    // Equity Indexes
    sp500: {
      id: "sp500",
      title: "S&P 500",
      icon: "📊",
      subtitle: "500 large U.S. companies",
      type: "fred",
      seriesId: "SP500",
      unit: "index",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8Nv",
      description:
        "Tracks 500 of the largest U.S. companies. When this is strong, it usually means broad “risk-on” appetite. If it rallies while small caps or cyclicals lag, leadership may be narrow and more fragile.",
    },
    nasdaq: {
      id: "nasdaq",
      title: "Nasdaq Composite",
      icon: "💻",
      subtitle: "Tech & growth-heavy index",
      type: "fred",
      seriesId: "NASDAQCOM",
      unit: "index",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8Pk",
      description:
        "Tech- and growth-heavy index with a lot of software, chip, and internet stocks. Big swings here often reflect changing views on growth, interest rates, and risk appetite rather than the whole economy.",
    },
    dow: {
      id: "dow",
      title: "Dow Jones",
      icon: "🏢",
      subtitle: "30 blue-chip stocks",
      type: "fred",
      seriesId: "DJIA",
      unit: "index",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8Px",
      description:
        "Thirty large, mature “blue-chip” companies. When the Dow outperforms a weak Nasdaq, money may be rotating into safer, dividend-paying names instead of high-growth tech.",
    },

    // Yields
    yield1m: {
      id: "yield1m",
      title: "1M Treasury",
      icon: "💰",
      subtitle: "1-month yield",
      type: "fred",
      seriesId: "DGS1MO",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8Qh",
      description:
        "Very short-term U.S. government rate that hugs the Fed funds rate. Fast moves here often signal changing expectations about near-term Fed hikes or cuts.",
    },
    yield3m: {
      id: "yield3m",
      title: "3M Treasury",
      icon: "💰",
      subtitle: "3-month yield",
      type: "fred",
      seriesId: "DGS3MO",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8QE",
      description:
        "Three-month Treasury yield, a classic cash proxy. When 3M yields jump above longer maturities, it suggests policy is tight and investors are being paid more to sit in cash than to take duration risk.",
    },
    yield6m: {
      id: "yield6m",
      title: "6M Treasury",
      icon: "💰",
      subtitle: "6-month yield",
      type: "fred",
      seriesId: "DGS6MO",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8QX",
      description:
        "Six-month Treasury yield that blends current Fed policy with near-term expectations. If 6M stays high while longer yields drop, the curve is flattening or inverting, a common late-cycle signal.",
    },
    yield2y: {
      id: "yield2y",
      title: "2Y Treasury",
      icon: "📊",
      subtitle: "2-year yield",
      type: "fred",
      seriesId: "DGS2",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8R5",
      description:
        "Two-year yield is a key market gauge of where the Fed policy rate is headed over the next couple of years. A sharp rise often means markets expect more hikes or fewer cuts.",
    },
    yield10y: {
      id: "yield10y",
      title: "10Y Treasury",
      icon: "📊",
      subtitle: "10-year yield",
      type: "fred",
      seriesId: "DGS10",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O5dK",
      description:
        "Benchmark long-term rate used for mortgages and corporate borrowing. Falling 10Y yields can mean growth fears or a flight to safety; rising 10Y yields can tighten financial conditions even if the Fed is on hold.",
    },
    yield30y: {
      id: "yield30y",
      title: "30Y Treasury",
      icon: "📊",
      subtitle: "30-year yield",
      type: "fred",
      seriesId: "DGS30",
      unit: "percent",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8Rm",
      description:
        "Ultra-long yield that reflects long-run views on inflation, growth, and fiscal risk. A steep 10Y–30Y slope is often about term premium and debt concerns more than near-term Fed policy.",
    },

    // Macro
    dxy: {
      id: "dxy",
      title: "Dollar Index",
      icon: "💵",
      subtitle: "Broad trade-weighted USD",
      type: "fred",
      seriesId: "DTWEXBGS",
      unit: "index",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8RE",
      description:
        "Broad, trade-weighted measure of the U.S. dollar versus major currencies. A stronger dollar usually pressures commodities and emerging markets but can help cool imported inflation for U.S. consumers.",
    },
    oil: {
      id: "oil",
      title: "Crude Oil WTI",
      icon: "🛢️",
      subtitle: "Spot price per barrel",
      type: "fred",
      seriesId: "DCOILWTICO",
      unit: "dollar",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8RH",
      description:
        "U.S. benchmark crude oil price per barrel. Rising oil is both an inflation story and a consumer-pressure story: it can lift headline CPI and squeeze household budgets, especially through gasoline.",
    },
    goldVol: {
      id: "goldVol",
      title: "Gold Volatility",
      icon: "🥇",
      subtitle: "Implied volatility in gold",
      type: "fred",
      seriesId: "GVZCLS",
      unit: "index",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1O8SF",
      description:
        "Implied volatility index for gold prices. Higher readings mean options markets expect bigger swings in gold, often during periods of stress, policy uncertainty, or strong moves in real interest rates.",
    },
    debt: {
      id: "debt",
      title: "US National Debt",
      icon: "🏛️",
      subtitle: "Total public debt (USD, trillions)",
      type: "fred",
      unit: "trillions",
      embedUrl: "https://fred.stlouisfed.org/graph/?g=1MapL",
      description:
        "Total public debt outstanding of the U.S. government, shown in trillions of dollars. It moves slowly, but a rising debt load can matter for long-term interest rates, fiscal policy debates, and market sentiment.",
    },
  };


// Fetch sparkline history (new)
const fetchOverviewHistory = async () => {
  try {
    const response = await fetch(apiUrl("/api/overview-history"));
    if (!response.ok) throw new Error("Failed to fetch overview history");
    const data = await response.json();
    setOverviewHistory(data);
  } catch (error) {
    console.error("Error fetching overview history:", error);
    setOverviewHistory(null);
  }
};

// Fetch main overview metrics (existing)
const fetchOverviewData = async () => {
  try {
    setLoading(true);
    const response = await fetch(apiUrl("/api/overview-metrics"));
    if (!response.ok) throw new Error("Failed to fetch data");
    const data = await response.json();

    setOverviewData(data);
    setMarketTone(computeMarketTone(data));
    onConnectionStatus?.(true);
  } catch (error) {
    console.error("Error fetching overview data:", error);
    onConnectionStatus?.(false);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchOverviewData();
  fetchOverviewHistory();

  const interval = setInterval(fetchOverviewData, 5 * 60 * 1000);
  const historyInterval = setInterval(fetchOverviewHistory, 15 * 60 * 1000);

  return () => {
    clearInterval(interval);
    clearInterval(historyInterval);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const handleCardClick = (metricConfig) => {
    setActiveMetric(metricConfig);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleInfoClick = (metricConfig) => {
    setActiveInfoMetric(metricConfig);
    setIsInfoOpen(true);
  };

  const closeInfoModal = () => setIsInfoOpen(false);

  // Tone "i" explanation modal (better layout)
  const openToneInfo = () => {
    const spxPct = marketTone?.inputs?.spxPct;
    const curve = marketTone?.inputs?.curve;
    const dxyPct = marketTone?.inputs?.dxyPct;
    const score = marketTone?.score;

    const fmtPct = (n) => (typeof n === "number" && Number.isFinite(n) ? `${n.toFixed(2)}%` : "—");
    const fmtNum = (n) => (typeof n === "number" && Number.isFinite(n) ? n.toFixed(2) : "—");

    setActiveInfoMetric({
      title: "Overall Market Tone (How we calculate it)",
      description: (
        <div>
          <p style={{ marginTop: 0 }}>
            This is a simple composite score using live metrics on this page. It’s a quick “risk-on vs risk-off”
            temperature check, not a trading signal.
          </p>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-light)" }}>
            <h4 style={{ margin: "0 0 8px", color: "var(--text-primary)" }}>Inputs</h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li><b>S&amp;P 500 daily %</b> (value vs previous value)</li>
              <li><b>Yield curve slope</b> = 10Y − 2Y</li>
              <li><b>Dollar daily % (DXY)</b> (value vs previous value)</li>
            </ul>
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-light)" }}>
            <h4 style={{ margin: "0 0 8px", color: "var(--text-primary)" }}>Scoring rules</h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>S&amp;P 500: {"> +0.40% = +1"} | {"< -0.40% = -1"}</li>
              <li>Curve (10Y−2Y): {"> +0.15 = +0.75"} | {"< -0.15 = -0.75"}</li>
              <li>DXY: {"> +0.25% = -0.5"} | {"< -0.25% = +0.5"}</li>
            </ul>
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-light)" }}>
            <h4 style={{ margin: "0 0 10px", color: "var(--text-primary)" }}>Today</h4>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ padding: 10, border: "1px solid var(--border-light)", borderRadius: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>S&amp;P 500 daily</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{fmtPct(spxPct)}</div>
              </div>

              <div style={{ padding: 10, border: "1px solid var(--border-light)", borderRadius: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Curve (10Y−2Y)</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{fmtNum(curve)}</div>
              </div>

              <div style={{ padding: 10, border: "1px solid var(--border-light)", borderRadius: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>DXY daily</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{fmtPct(dxyPct)}</div>
              </div>

              <div style={{ padding: 10, border: "1px solid var(--border-light)", borderRadius: 12 }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Total score</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)" }}>{fmtNum(score)}</div>
              </div>
            </div>

            <p style={{ marginTop: 12, marginBottom: 0 }}>
              Thresholds: <b>Risk-On ≥ 1.25</b>, <b>Risk-Off ≤ -1.25</b>, otherwise <b>Neutral</b>.
            </p>
          </div>
        </div>
      ),
    });

    setIsInfoOpen(true);
  };

  if (loading && !overviewData) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner"></div>
        <p>Loading market data...</p>
      </div>
    );
  }

  return (
    <div className="overview">
      {/* Overall Market Tone */}
      <div className="summary-card market-tone market-tone-card">
        <button
          type="button"
          className="metric-info-button market-tone-info"
          onClick={openToneInfo}
          aria-label="How we calculate Overall Market Tone"
          title="How we calculate this"
        >
          i
        </button>

        <h3>Overall Market Tone: {marketTone.label}</h3>
        <p>
          {marketTone.detail ||
            "Major indexes and yields give a quick macro view: stocks (top), rates (middle), and macro (bottom)."}
        </p>
      </div>

      {/* Block A: Equity Indexes */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Equity Indexes (U.S. stocks)</h2>
          <p>These big indexes show how different parts of the U.S. stock market are doing.</p>
        </div>

        <div className="metrics-grid">
          <MetricCard
          metric={metricsConfig.sp500}
          data={overviewData?.sp500}
          onClick={handleCardClick}
          onInfoClick={handleInfoClick}
          sparklineData={overviewHistory?.sp500}
          sparklineLabel="Last 3 months"
          />
          <MetricCard
          metric={metricsConfig.nasdaq}
          data={overviewData?.nasdaq}
          onClick={handleCardClick}
          onInfoClick={handleInfoClick}
          sparklineData={overviewHistory?.nasdaq}
          sparklineLabel="Last 3 months"
          />
          <MetricCard
          metric={metricsConfig.dow}
          data={overviewData?.dow}
          onClick={handleCardClick}
          onInfoClick={handleInfoClick}
          sparklineData={overviewHistory?.dow}
          sparklineLabel="Last 3 months"
          />
        </div>

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              When S&amp;P 500 and Nasdaq move together, that&apos;s broad &quot;risk-on&quot; or &quot;risk-off.&quot;
              If Dow is strong while Nasdaq lags, money may be rotating into safer, blue-chip names.
            </p>
          </div>
        </div>
      </section>

      {/* Block B: Yield Curve */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Yield Curve (short vs long rates)</h2>
          <p>Short-term yields follow Fed policy; long-term yields reflect growth and inflation expectations.</p>
        </div>

        <YieldCurveChart data={overviewData} />

        <div className="yield-metrics-grid" style={{ marginTop: "24px" }}>
          <MetricCard metric={metricsConfig.yield1m} data={overviewData?.yield1m} onClick={handleCardClick} onInfoClick={handleInfoClick} />
          <MetricCard metric={metricsConfig.yield3m} data={overviewData?.yield3m} onClick={handleCardClick} onInfoClick={handleInfoClick} />
          <MetricCard metric={metricsConfig.yield6m} data={overviewData?.yield6m} onClick={handleCardClick} onInfoClick={handleInfoClick} />
          <MetricCard metric={metricsConfig.yield2y} data={overviewData?.yield2y} onClick={handleCardClick} onInfoClick={handleInfoClick} />
          <MetricCard metric={metricsConfig.yield10y} data={overviewData?.yield10y} onClick={handleCardClick} onInfoClick={handleInfoClick} />
          <MetricCard metric={metricsConfig.yield30y} data={overviewData?.yield30y} onClick={handleCardClick} onInfoClick={handleInfoClick} />
        </div>

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              If short-term yields (like 3M or 6M) are higher than long-term yields (like 10Y or 30Y), the curve is inverted,
              which often signals recession risk. A steep curve (long yields much higher than short) usually points to healthier
              growth expectations.
            </p>
          </div>
        </div>
      </section>

      {/* Block C: Macro Overview */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Macro Overview (FX, commodities, debt)</h2>
          <p>These metrics help you understand the macro forces behind risk assets.</p>
        </div>

        <div className="metrics-grid">
          <MetricCard
            metric={metricsConfig.dxy}
            data={overviewData?.dxy}
            onClick={handleCardClick}
            onInfoClick={handleInfoClick}
            sparklineData={overviewHistory?.dxy}
            sparklineLabel="Last 3 months"
          />
          <MetricCard
            metric={metricsConfig.oil}
            data={overviewData?.oil}
            onClick={handleCardClick}
            onInfoClick={handleInfoClick}
            sparklineData={overviewHistory?.oil}
            sparklineLabel="Last 3 months"
          />
          <MetricCard
            metric={metricsConfig.goldVol}
            data={overviewData?.goldVol}
            onClick={handleCardClick}
            onInfoClick={handleInfoClick}
            sparklineData={overviewHistory?.goldVol}
            sparklineLabel="Last 3 months"
          />
          <MetricCard
            metric={metricsConfig.debt}
            data={overviewData?.debt}
            onClick={handleCardClick}
            onInfoClick={handleInfoClick}
            sparklineData={overviewHistory?.debt}
            sparklineLabel="Last 12 pts"
          />
        </div>

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              Stronger USD often pressures commodities like oil and gold. Rising oil can feed into inflation and hurt consumers.
              Higher U.S. debt shapes long-term fiscal and rate expectations, even if it moves slowly.
            </p>
          </div>
        </div>
      </section>

      {/* Chart modal */}
      <ChartModal isOpen={isModalOpen} metric={activeMetric} onClose={closeModal} />

      {/* Info modal */}
      <InfoModal isOpen={isInfoOpen} metric={activeInfoMetric} onClose={closeInfoModal} />
    </div>
  );
};

export default Overview;

/**
 * Lightweight Info Modal (no dependency on modal CSS classes)
 * Uses inline styles so it works immediately even if you don’t have modal styles.
 */
const InfoModal = ({ isOpen, metric, onClose }) => {
  if (!isOpen || !metric) return null;

  const backdropStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  };

  const panelStyle = {
    width: "min(720px, 92vw)",
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: 14,
    boxShadow: "var(--shadow)",
    overflow: "hidden",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle = {
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid var(--border-light)",
  };

  const titleStyle = {
    margin: 0,
    fontSize: "1.05rem",
    color: "var(--text-primary)",
  };

  const closeStyle = {
    border: "none",
    background: "transparent",
    color: "var(--text-secondary)",
    fontSize: 22,
    cursor: "pointer",
    lineHeight: 1,
  };

  const bodyStyle = {
    padding: "16px 18px 18px",
    color: "var(--text-secondary)",
    lineHeight: 1.6,
    overflowY: "auto",
  };

  // Support string OR JSX for description
  const content =
    typeof metric.description === "string" ? metric.description : <div>{metric.description}</div>;

  return (
    <div style={backdropStyle} onClick={onClose} role="dialog" aria-modal="true">
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>How to read: {metric.title}</h2>
          <button style={closeStyle} onClick={onClose} aria-label="Close info">
            ×
          </button>
        </div>
        <div style={bodyStyle}>{content || "No description configured for this metric yet."}</div>
      </div>
    </div>
  );
};